/* ============================================================
   VIP Numbers — full-catalogue instant number search  (v5.2 Hybrid)
   ------------------------------------------------------------
   Searches Available Numbers + Pre-Order Numbers by ANY digit
   sequence (e.g. "010101" finds 07337010101), client-side.

   v4.x history: static index (assets/vip-num-index.json), background
   re-crawl, BUY button, family bundles, GA4 via Shopify.analytics,
   tappable "add more digits" note, idle-time prep (v4.8).

   v5.0 Hybrid Search (13 Oct 2026, built in unpublished test theme):
   - FIX: zero-results bug. v4.8 prepared items only in
     requestIdleCallback slices and gave up whenever the browser
     reported 0 ms idle time (busy pages, preview bar, background
     tabs), so nothing ever became searchable. Items are now
     prepared on demand inside search() and warmed in fixed-size
     setTimeout chunks that always make progress.
   - Instant results from the local index (browser cache, else the
     static CDN index), then LIVE VERIFICATION of the numbers being
     shown via Shopify's tokenless Storefront API (one request for
     up to 250 numbers). Deleted / archived numbers ("not found")
     and unavailable numbers are removed before display.
   - BUY is only active on numbers verified live within VERIFY_TTL.
     Unverified numbers never show an active BUY: while checking
     the button shows "…" (disabled); if the check fails the row
     shows "VIEW" and opens the product page.
   - Automatic top-up of newly published / changed numbers (Storefront
     API, newest-updated first, any batch size up to TOPUP_MAX_PAGES
     x 250; larger batches trigger a full background refresh).
   - Automatic background refresh of Available (every 6 h) and
     Pre-Order (every 24 h) from /collections/*?view=num-index,
     replacing the frozen static index in the browser cache.
   - Failed BUY: "SOLD" if the number is no longer available,
     "RETRY" for network errors.
   Design, matching rules, groups, cart flow and GA4 event names /
   parameters are unchanged.

   v5.3 (26 Oct 2026, v140): a closed results panel stays closed. Background
   refreshes, live availability checks and returning to the page (tab/app switch,
   Back button) no longer re-open previous results; results appear again only
   when the customer types in a search box. The search term is kept.

   v5.4 (27 Oct 2026, v142): the "0 RESULTS" message also links to Custom Numbers
   ("Choose your own last 6 digits"). Nothing else changed.

   v6.0 (Nov 2026, draft v162 – Search Tracking): GA4 reporting only (see the
   GA4 analytics section). Search, results, highlighting, design and BUY unchanged.

   Requires: templates/collection.num-index.liquid
   ============================================================ */
(function () {
  'use strict';
  if (window.__vipNumSearchLoaded) return;
  window.__vipNumSearchLoaded = true;

  var COLLECTIONS = [
    { handle: 'available-numbers', badge: 'AVAILABLE' },
    { handle: 'pre-order-numbers', badge: 'PRE-ORDER' }
  ];
  var CACHE_KEY = 'vipNumIndex_v3';
  var OLD_CACHE_KEYS = ['vipNumIndex_v2'];
  var CACHE_MAX_AGE = 24 * 60 * 60 * 1000;   /* use cached index for instant results up to 24 h */
  var AVAIL_REFRESH = 6 * 60 * 60 * 1000;    /* full background refresh of Available (top-up + live checks cover the time in between) */
  var PRE_REFRESH = 24 * 60 * 60 * 1000;     /* full background refresh of Pre-Order */
  var TOPUP_EVERY = 5 * 60 * 1000;           /* incremental "updated since" check */
  var TOPUP_MAX_PAGES = 12;                  /* 12 x 250 = 3,000 changed products */
  var MAX_ATTEMPTS = 4;                      /* per collection page, with backoff */
  var MIN_DIGITS = 3;
  var MAX_PER_GROUP = 150;

  var SF_API = '/api/2025-07/graphql.json'; /* tokenless Storefront API, same origin */
  var VERIFY_CHUNK = 250;                    /* ids per Storefront request */
  var VERIFY_TTL = 3 * 60 * 1000;            /* trust a live check for 3 minutes */
  var VERIFY_WAIT = 700;                     /* wait this long for the check before showing results */
  var VERIFY_TIMEOUT = 4000;                 /* give up on a check after this long */
  var BUY_RECHECK = 60 * 1000;               /* re-check a number on BUY if older than this */

  var GOLD = '#d4b85e', BG = '#0f0e0a', BORDER = 'rgba(212,184,94,0.35)';
  var MUTED = '#7a7068';

  var groups = null;        /* [{badge, items, expected, failed}] */
  var meta = null;          /* {built, availTs, preTs, since} */
  var status = 'idle';      /* idle | loading | ready */
  var refreshing = false;   /* any background refresh running */
  var grandExpected = 0, donePages = 0, totalPages = 0;
  var startPromise = null;
  var panel = null, activeInput = null, lastQuery = '';
  var renderTimer = null, maintTimer = null;
  var vidIndex = null;      /* vid -> {gi, item} */

  /* live verification state: vid -> {s:'ok'|'gone'|'na', t:ms} */
  var vstate = {};
  var inflight = {};        /* vid -> Promise */
  var verifyBroken = false; /* last verification attempt failed */
  var renderSeq = 0;
  var userClosed = false;   /* v5.3: panel was closed; only new typing re-opens it */

  /* ---------------- utils ---------------- */
  function now() { return Date.now(); }
  function delay(ms) { return new Promise(function (res) { setTimeout(res, ms); }); }

  /* ---------------- cache ---------------- */
  function fromCache() {
    try {
      OLD_CACHE_KEYS.forEach(function (k) { try { localStorage.removeItem(k); } catch (e) { } });
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || !o.groups || !o.meta || (now() - o.ts) > CACHE_MAX_AGE) return null;
      if (!o.groups.length || !o.groups[0].items) return null;
      return o;
    } catch (e) { return null; }
  }
  function toCache() {
    if (!groups || !meta) return;
    try {
      /* _segs is a non-index property, so JSON.stringify drops it automatically */
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: now(), meta: meta, groups: groups }));
    } catch (e) { /* storage full/blocked — search still works this session */ }
  }

  /* ---------------- item helpers ---------------- */
  function prepItem(it) {
    it[0] = String(it[0]).replace(/&amp;/g, '&');
    /* v5.1: one segment per number ('&' separates family numbers); spaces and other
       separators inside a number are ignored, so "077 08 008 008" matches 07708008008 */
    it._segs = it[0].split('&').map(function (p) { return p.replace(/\D/g, ''); })
      .filter(function (s) { return s.length > 0; });
  }
  function itemVid(it) {
    return (typeof it[2] === 'number') ? it[2] : null;
  }
  function itemHandle(it) {
    if (typeof it[2] === 'string') return it[2];
    if (typeof it[3] === 'string') return it[3];
    return it[0];
  }

  /* Warm-up preparation in fixed chunks that ALWAYS make progress
     (replaces v4.8 idle-callback prep, which could stall forever). */
  function warmPrep(onDone) {
    if (!groups) return;
    var gi = 0, ii = 0, CHUNK = 3000;
    function step() {
      var n = 0;
      while (gi < groups.length && n < CHUNK) {
        var items = groups[gi].items;
        while (ii < items.length && n < CHUNK) {
          if (!items[ii]._segs) prepItem(items[ii]);
          ii++; n++;
        }
        if (ii >= items.length) { gi++; ii = 0; }
      }
      if (gi < groups.length) setTimeout(step, 0);
      else if (onDone) onDone();
    }
    setTimeout(step, 0);
  }

  function rebuildVidIndex() {
    vidIndex = {};
    if (!groups) return;
    groups.forEach(function (g, gi) {
      for (var i = 0; i < g.items.length; i++) {
        var v = itemVid(g.items[i]);
        if (v) vidIndex[v] = { gi: gi, item: g.items[i] };
      }
    });
  }

  function recount() {
    grandExpected = 0;
    if (groups) groups.forEach(function (g) {
      grandExpected += (g.expected > 0 ? g.expected : g.items.length);
    });
  }

  function isDead(it) {
    var v = itemVid(it);
    if (!v) return false;
    var st = vstate[v];
    return !!(st && (st.s === 'gone' || st.s === 'na'));
  }
  function isVerified(it) {
    var v = itemVid(it);
    if (!v) return false;
    var st = vstate[v];
    return !!(st && st.s === 'ok' && (now() - st.t) < VERIFY_TTL);
  }

  /* ---------------- collection pages (num-index view) ---------------- */
  function fetchPage(handle, page, attempt) {
    attempt = attempt || 0;
    return fetch('/collections/' + handle + '?view=num-index&page=' + page,
      { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .catch(function (err) {
        if (attempt < MAX_ATTEMPTS - 1) {
          var wait = [700, 1600, 3400][attempt] || 3400;
          wait += Math.random() * 400;
          return delay(wait).then(function () { return fetchPage(handle, page, attempt + 1); });
        }
        throw err;
      });
  }

  /* Full crawl of one collection. Resolves with a fresh group, or null on failure. */
  function crawlGroup(gi, concurrency) {
    var c = COLLECTIONS[gi];
    var fresh = { badge: c.badge, items: [], expected: 0, failed: false };
    var ok = true;
    return fetchPage(c.handle, 1).then(function (first) {
      fresh.expected = first.total || 0;
      (first.items || []).forEach(function (it) { fresh.items.push(it); });
      var pages = first.pages || 1, queue = [];
      for (var p = 2; p <= pages; p++) queue.push(p);
      return new Promise(function (resolve) {
        var i = 0, active = 0;
        function pump() {
          if (i >= queue.length && active === 0) return resolve();
          while (active < concurrency && i < queue.length) {
            (function (pg) {
              active++;
              fetchPage(c.handle, pg)
                .then(function (d) { ((d && d.items) || []).forEach(function (it) { fresh.items.push(it); }); })
                .catch(function () { ok = false; })
                .then(function () { active--; pump(); });
            })(queue[i++]);
          }
        }
        pump();
      });
    }).then(function () {
      if (ok && fresh.expected > 0 && fresh.items.length >= fresh.expected) return fresh;
      return null;
    }).catch(function () { return null; });
  }

  /* ---------------- static index ---------------- */
  function staticIndexUrl() {
    var s = document.querySelector('script[src*="vip-number-search"]');
    if (!s) return null;
    var src = s.getAttribute('src');
    var i = src.indexOf('vip-number-search');
    if (i === -1) return null;
    var d = new Date();
    var stamp = '' + d.getFullYear() + (d.getMonth() + 101 + '').slice(1) + (d.getDate() + 100 + '').slice(1);
    /* Shopify's CDN ignores unknown query parameters when caching, so "?d=" never
       refreshed the cached index. It does key on "?v=", so a daily v= stamp makes
       a rebuilt index reach visitors within a day. */
    return src.slice(0, i) + 'vip-num-index.json?v=' + stamp;
  }

  function tryStatic() {
    var url = staticIndexUrl();
    if (!url) return Promise.reject(new Error('no url'));
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    }).then(function (data) {
      var g = data && data.groups;
      if (!g || !g.length || !g[0].items || !g[0].items.length) throw new Error('bad index');
      return { groups: g, built: data.built || null };
    });
  }

  /* ---------------- first-load fallback: live crawl of both ---------------- */
  function startCrawl() {
    status = 'loading';
    totalPages = 2; donePages = 0;
    groups = COLLECTIONS.map(function (c) { return { badge: c.badge, items: [], expected: 0, failed: false }; });
    var t0 = new Date().toISOString();
    return Promise.all([crawlGroup(0, 4), crawlGroup(1, 4)]).then(function (res) {
      res.forEach(function (fresh, gi) {
        if (fresh) groups[gi] = fresh; else groups[gi].failed = true;
        donePages++;
      });
      meta = { built: t0, availTs: res[0] ? Date.parse(t0) : 0, preTs: res[1] ? Date.parse(t0) : 0, since: null };
      afterGroupsChanged();
      status = 'ready';
      if (res[0] && res[1]) toCache();
      scheduleRender(0);
      scheduleMaintenance(3000);
    });
  }

  function afterGroupsChanged() {
    recount();
    rebuildVidIndex();
  }

  function startLoading() {
    if (status !== 'idle') return startPromise;

    /* --- browser cache path (instant) --- */
    var cached = fromCache();
    if (cached) {
      groups = cached.groups;
      meta = cached.meta;
      groups.forEach(function (g) { g.failed = false; });
      afterGroupsChanged();
      status = 'ready';
      startPromise = Promise.resolve();
      warmPrep(function () { scheduleRender(0); });
      scheduleMaintenance(1500);
      return startPromise;
    }

    /* --- static index path (CDN, fast) --- */
    status = 'loading';
    startPromise = tryStatic().then(function (res) {
      groups = res.groups;
      groups.forEach(function (grp) { grp.failed = false; });
      meta = { built: res.built, availTs: 0, preTs: 0, since: null };
      afterGroupsChanged();
      status = 'ready';
      scheduleRender(0);
      warmPrep(function () { scheduleRender(0); });
      scheduleMaintenance(1000);
    }).catch(function () {
      return startCrawl();
    });
    return startPromise;
  }

  function loadedCount() {
    var n = 0;
    if (groups) groups.forEach(function (g) { n += g.items.length; });
    return n;
  }
  function isIncomplete() {
    if (!groups) return true;
    var bad = false;
    groups.forEach(function (g) {
      if (g.failed || (g.expected > 0 && g.items.length < g.expected)) bad = true;
    });
    return bad;
  }
  function pct() {
    if (!totalPages) return 5;
    return Math.min(99, Math.round(donePages / totalPages * 100));
  }

  /* ---------------- Storefront API (tokenless) ---------------- */
  function sfQuery(query, variables, timeoutMs) {
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = null;
    var req = fetch(SF_API, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ query: query, variables: variables || {} }),
      signal: ctrl ? ctrl.signal : undefined
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    }).then(function (j) {
      if (!j || j.errors || !j.data) throw new Error('graphql');
      return j.data;
    });
    var to = new Promise(function (res, rej) {
      timer = setTimeout(function () {
        if (ctrl) { try { ctrl.abort(); } catch (e) { } }
        rej(new Error('timeout'));
      }, timeoutMs || VERIFY_TIMEOUT);
    });
    return Promise.race([req, to]).then(function (d) { clearTimeout(timer); return d; },
      function (e) { clearTimeout(timer); throw e; });
  }

  var VERIFY_Q = 'query($ids:[ID!]!){nodes(ids:$ids){... on ProductVariant{id availableForSale}}}';

  /* Verify a list of variant ids live. Resolves true if every request succeeded. */
  function verifyVids(vids) {
    var need = [], waits = [];
    vids.forEach(function (v) {
      var st = vstate[v];
      if (st && (now() - st.t) < VERIFY_TTL) return;
      if (inflight[v]) { waits.push(inflight[v]); return; }
      need.push(v);
    });
    var chunks = [];
    for (var i = 0; i < need.length; i += VERIFY_CHUNK) chunks.push(need.slice(i, i + VERIFY_CHUNK));
    var reqs = chunks.map(function (chunk) {
      var p = sfQuery(VERIFY_Q, {
        ids: chunk.map(function (v) { return 'gid://shopify/ProductVariant/' + v; })
      }).then(function (data) {
        var t = now();
        var nodes = data.nodes || [];
        chunk.forEach(function (v, k) {
          var n = nodes[k];
          if (!n) vstate[v] = { s: 'gone', t: t };
          else vstate[v] = { s: n.availableForSale ? 'ok' : 'na', t: t };
        });
        return true;
      }).catch(function () { return false; })
        .then(function (ok) { chunk.forEach(function (v) { delete inflight[v]; }); return ok; });
      chunk.forEach(function (v) { inflight[v] = p; });
      return p;
    });
    return Promise.all(reqs.concat(waits)).then(function (res) {
      var ok = res.every(function (x) { return x !== false; });
      if (reqs.length) verifyBroken = !ok;
      return ok;
    });
  }

  /* ---------------- background maintenance ---------------- */
  var TOPUP_Q =
    'query($q:String!,$after:String){products(first:250,after:$after,sortKey:UPDATED_AT,reverse:true,query:$q){' +
    'pageInfo{hasNextPage endCursor} nodes{handle title tags updatedAt availableForSale ' +
    'variants(first:1){nodes{id availableForSale price{amount}}}}}}';

  function gidNum(gid) {
    var m = String(gid || '').match(/(\d+)$/);
    return m ? parseInt(m[1], 10) : null;
  }

  /* Incremental update: products published or changed since meta.since. */
  function topUp() {
    if (!groups || !meta) return Promise.resolve(false);
    var since = meta.since;
    if (!since) return Promise.resolve(false);
    var started = new Date(now() - 2 * 60 * 1000).toISOString(); /* small overlap */
    var q = 'updated_at:>\'' + since + '\'';
    var pages = 0, changed = 0, overflow = false;

    function page(after) {
      pages++;
      return sfQuery(TOPUP_Q, { q: q, after: after || null }, 8000).then(function (data) {
        var conn = data.products;
        (conn.nodes || []).forEach(function (p) { if (applyProduct(p)) changed++; });
        if (conn.pageInfo && conn.pageInfo.hasNextPage) {
          if (pages >= TOPUP_MAX_PAGES) { overflow = true; return; }
          return page(conn.pageInfo.endCursor);
        }
      });
    }

    return page(null).then(function () {
      meta.since = started;
      if (overflow) { meta.availTs = 0; meta.preTs = 0; } /* too many changes: force full refresh */
      if (changed) { afterGroupsChanged(); scheduleRender(0); }
      toCache();
      return true;
    }).catch(function () { return false; });
  }

  /* Merge one Storefront product into the local index. Returns true if anything changed. */
  function applyProduct(p) {
    var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
    if (tags.indexOf('esim') !== -1) return false;
    var vnode = p.variants && p.variants.nodes && p.variants.nodes[0];
    if (!vnode) return false;
    var vid = gidNum(vnode.id);
    if (!vid) return false;
    var gi = (tags.indexOf('preorder') !== -1) ? 1 : 0;
    var avail = !!(vnode.availableForSale && p.availableForSale !== false);
    vstate[vid] = { s: avail ? 'ok' : 'na', t: now() };
    var price = Number(vnode.price && vnode.price.amount) || 0;
    var existing = vidIndex && vidIndex[vid];
    if (existing) {
      var it = existing.item, changed = false;
      if (it[1] !== price) { it[1] = price; changed = true; }
      if (existing.gi !== gi) {
        var arr = groups[existing.gi].items, k = arr.indexOf(it);
        if (k !== -1) arr.splice(k, 1);
        groups[gi].items.push(it);
        changed = true;
      }
      return changed;
    }
    if (!avail) return false;
    var item = [p.title, price, vid];
    if (p.handle && p.handle !== p.title) item.push(p.handle);
    prepItem(item);
    groups[gi].items.push(item);
    vidIndex[vid] = { gi: gi, item: item };
    return true;
  }

  function refreshGroup(gi, concurrency) {
    var started = now();
    return crawlGroup(gi, concurrency).then(function (fresh) {
      if (!fresh || !groups) return false;
      groups[gi] = fresh;
      /* store the crawl START time: anything changed after it is caught by topUp */
      if (gi === 0) meta.availTs = started; else meta.preTs = started;
      afterGroupsChanged();
      warmPrep(function () { scheduleRender(0); });
      toCache();
      return true;
    });
  }

  function maintenance() {
    if (!groups || !meta || refreshing) return;
    if (document.visibilityState === 'hidden') { scheduleMaintenance(TOPUP_EVERY); return; }
    refreshing = true;
    var chain = Promise.resolve();
    /* 1. full refreshes when due (Available every 30 min, Pre-Order every 24 h) */
    chain = chain.then(function () {
      if ((now() - (meta.availTs || 0)) > AVAIL_REFRESH) return refreshGroup(0, 4);
    }).then(function () {
      if ((now() - (meta.preTs || 0)) > PRE_REFRESH) return refreshGroup(1, 2);
    }).then(function () {
      /* 2. incremental top-up of anything published/changed since the last refresh */
      if (!meta.availTs || !meta.preTs) return;
      if (!meta.since) meta.since = new Date(Math.min(meta.availTs, meta.preTs) - 2 * 60 * 1000).toISOString();
      if ((now() - (meta.lastTopUp || 0)) < TOPUP_EVERY) return;
      return topUp().then(function (ok) { if (ok) { meta.lastTopUp = now(); toCache(); } });
    });
    chain.then(function () { refreshing = false; scheduleRender(0); scheduleMaintenance(TOPUP_EVERY); },
      function () { refreshing = false; scheduleMaintenance(TOPUP_EVERY); });
  }

  function scheduleMaintenance(ms) {
    clearTimeout(maintTimer);
    maintTimer = setTimeout(maintenance, ms || 0);
  }

  /* ---------------- GA4 analytics (via Shopify Custom Pixel bridge) ----------------
     v6.0 tracking (track_v "2", draft for approval). Search behaviour, results, design and BUY are unchanged;
     only what is reported to GA4 changes. Everything still goes through Shopify.analytics.publish, so the
     "VIP Numbers GA4" custom pixel's analytics-consent gate still decides whether anything is sent.
     - Leading zeros: GA4 turns digit-only parameter values into numbers ("07777" was reported as 7777).
       Terms and numbers are now sent as text that can never be read as a number ("#07777"), plus search_len.
     - Privacy: queries of 8+ digits are always masked to their first 3 digits ("#077xxxxxxxx"), including
       our own stock. Click and BUY events carry the Shopify variant ID, not the number. A searched
       number may be someone's personal mobile number, which Google Analytics must not receive. Full-number
       searches instead carry inventory_match (exact_available / exact_preorder / none) and tail_pattern.
       Letters are never sent (text-only searches are reported as "(text)" with their length only).
     - One event per completed search intention, not per pause while typing: an intention is committed
       after 2 s without typing, or at once when the customer clicks a result, taps BUY, presses Enter,
       leaves the box, closes the results or leaves the page. Each intention carries a random search_id
       (one search-box visit; nothing stored on the device), its order, how it relates to the previous one
       (first / longer / shorter / changed / same), whether it repeats an earlier search in the same visit
       (repeat_search yes/no) and why it was committed.
     - Input style without raw text: input_format (digits, spaced, plus44, intl0044, intl44, mixed_text, text)
       and input_method (typed, paste, autofill). */
  var COMMIT_IDLE = 2000, SID_IDLE = 30 * 60 * 1000, MASK_FROM = 8;
  var sid = null, sidAt = 0, intentN = 0, lastCommit = null, pending = null, commitTimer = null, commitTries = 0, boxCleared = false, shownCounts = null;

  function track(name, params) {
    try {
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, params || {});
      }
    } catch (e) { }
  }
  function newSid() {
    sid = now().toString(36) + Math.random().toString(36).slice(2, 8);
    intentN = 0; lastCommit = null;
  }
  function touchSid() { if (!sid || now() - sidAt > SID_IDLE) newSid(); sidAt = now(); }

  function inputFormat(raw) {
    var s = String(raw || '').trim();
    var hasL = /[a-z]/i.test(s), hasD = /\d/.test(s);
    if (hasL && !hasD) return 'text';
    if (hasL) return 'mixed_text';
    if (/^\+\s*44/.test(s)) return 'plus44';
    var d = digitsOf(s);
    if (/^0044/.test(d)) return 'intl0044';
    if (/^440?7\d{9}$/.test(d)) return 'intl44';
    if (/\D/.test(s)) return 'spaced';
    return 'digits';
  }
  function queryClass(d, fmt) {
    if (fmt === 'text') return 'text';
    var l = d.length;
    if (l === 11 && /^07/.test(d)) return 'full_uk_mobile';
    if (l >= 12) return 'long_12plus';
    if (l >= 8) return 'partial_8_10';
    if (l === 7) return 'pattern_7';
    if (l >= 5) return 'pattern_5_6';
    return 'pattern_' + l;
  }
  /* shape of the last 6 digits of a long query (never the digits themselves) */
  function tailPattern(d) {
    var t = d.slice(-6);
    if (d.length < MASK_FROM || t.length < 6) return '';
    var a = t.split('');
    if (/^(\d)\1{5}$/.test(t)) return 'AAAAAA';
    if (/^(\d)\1{4}\d$|^\d(\d)\2{4}$/.test(t)) return 'AAAAA_run';
    if (/^(\d\d)\1\1$/.test(t)) return 'ABABAB';
    if (/^(\d{3})\1$/.test(t)) return 'ABCABC';
    if (a[0] === a[1] && a[2] === a[3] && a[4] === a[5]) return 'AABBCC';
    if (/^(\d)\1\1(\d)\2\2$/.test(t)) return 'AAABBB';
    var up = true, down = true;
    for (var i = 1; i < 6; i++) { if (+a[i] !== +a[i - 1] + 1) up = false; if (+a[i] !== +a[i - 1] - 1) down = false; }
    if (up || down) return 'sequential';
    var y = +t.slice(2);
    if ((y >= 1940 && y <= 2035)) return 'year_ending';
    if (/(\d)\1{3}/.test(t)) return 'four_same';
    return 'other';
  }
  /* full-number searches only: is it exactly one of our listed numbers? (a flag, never the number) */
  function inventoryMatch(d, cls) {
    if (cls !== 'full_uk_mobile') return 'not_full_number';
    if (!groups) return 'unknown';
    for (var g = 0; g < groups.length; g++) {
      var items = groups[g].items;
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it._segs) prepItem(it);
        if (it._segs.indexOf(d) !== -1 && !isDead(it)) return g === 0 ? 'exact_available' : 'exact_preorder';
      }
    }
    return 'none';
  }
  function stockBand(n) {
    return n === 0 ? '0' : n <= 5 ? '1-5' : n <= 20 ? '6-20' : n <= 100 ? '21-100' : '100+';
  }
  /* text GA4 cannot read as a number; long queries masked unless they are our own Available stock */
  function safeTerm(d, cls) {
    if (cls === 'text') return '(text)';
    if (d.length < MASK_FROM) return '#' + d;   /* 8+ digits are always masked, even our own stock */
    /* conservative: only the first 3 digits are kept, so even with tail_pattern a full number stays one of
       at least 1,000 possible numbers (e.g. #077xxxxxxxx + AAAAAA = 077 + 2 unknown digits + 10 endings) */
    return '#' + d.slice(0, 3) + new Array(d.length - 3 + 1).join('x');
  }
  /* click / BUY: the Shopify variant ID of our product, never the phone number itself */
  function variantIdFor(title) {
    if (!groups || !title) return '';
    for (var g = 0; g < groups.length; g++) {
      var items = groups[g].items;
      for (var i = 0; i < items.length; i++) {
        if (items[i][0] === title) { var v = itemVid(items[i]); return v ? String(v) : ''; }
      }
    }
    return '';
  }

  /* called on every (debounced) change of a search box */
  function noteInput(input, q) {
    var raw = String(input.value || '');
    var fmt = inputFormat(raw);
    var isText = fmt === 'text' && raw.trim().length >= 3;
    if (!isText && q.length < MIN_DIGITS) {           /* box emptied or too short: drop the unfinished intention */
      clearTimeout(commitTimer); pending = null; if (!raw.trim()) boxCleared = true; return;
    }
    var method = (pending && pending.method !== 'typed') ? pending.method : (input.__vipLastInputType || 'typed');
    pending = { d: isText ? '' : q, fmt: fmt, method: method, textLen: isText ? raw.trim().length : 0, afterClear: boxCleared };
    clearTimeout(commitTimer); commitTries = 0;
    commitTimer = setTimeout(function () { commitSearch('idle'); }, COMMIT_IDLE);
  }

  function commitSearch(reason) {
    clearTimeout(commitTimer);
    var p = pending;
    if (!p) return;
    var ready = status === 'ready';
    if (p.fmt !== 'text' && !ready && reason !== 'leave' && commitTries < 6) {
      /* index still loading: wait for real counts (up to ~12 s), but never drop the search */
      commitTries++;
      commitTimer = setTimeout(function () { commitSearch(reason); }, 2000);
      return;
    }
    pending = null;
    touchSid();
    var cls = queryClass(p.d, p.fmt);
    var key = cls === 'text' ? 'text:' + p.textLen : p.d;
    /* same query, never cleared in between (e.g. 07777 -> 077779 -> back to 07777): nothing new */
    if (lastCommit && lastCommit.key === key && !p.afterClear) return;
    boxCleared = false;
    var refine = 'first';
    if (lastCommit) {
      if (lastCommit.key === key) refine = 'same';
      else if (cls !== 'text' && lastCommit.d && p.d.indexOf(lastCommit.d) !== -1) refine = 'longer';
      else if (cls !== 'text' && lastCommit.d && lastCommit.d.indexOf(p.d) !== -1) refine = 'shorter';
      else refine = 'changed';
    }
    var a = 0, pr = 0, known = cls === 'text' || ready;
    if (cls !== 'text' && known) {
      /* counts the customer was shown (no extra search on click / BUY); recomputed only if not on screen */
      if (shownCounts && shownCounts.q === p.d) { a = shownCounts.a; pr = shownCounts.p; }
      else { var res = search(p.d); if (res[0]) a = res[0].hits.length; if (res[1]) pr = res[1].hits.length; }
    }
    intentN++;
    var term = safeTerm(p.d, cls);
    track('number_search', {
      search_term: term, search_len: cls === 'text' ? p.textLen : p.d.length, query_class: cls,
      input_format: p.fmt, input_method: p.method, search_id: sid, intent_seq: intentN,
      refine_type: refine, repeat_search: (lastCommit && lastCommit.seen[key]) ? 'yes' : 'no', commit_reason: reason, tail_pattern: cls === 'text' ? '' : tailPattern(p.d),
      inventory_match: cls === 'text' ? 'not_full_number' : (ready ? inventoryMatch(p.d, cls) : 'unknown'),
      stock_band: cls === 'text' ? '' : (known ? stockBand(a) : 'unknown'),
      /* counts left out (not 0) if the customer left before the stock list finished loading */
      available_hits: known ? a : undefined, preorder_hits: known ? pr : undefined, total_hits: known ? a + pr : undefined, track_v: '2'
    });
    if (cls !== 'text' && known && a + pr === 0) track('number_search_zero', { search_term: term, search_len: p.d.length, query_class: cls, search_id: sid, track_v: '2' });
    var seen = lastCommit ? lastCommit.seen : {}; seen[key] = 1;
    lastCommit = { key: key, d: p.d, term: term, cls: cls, seen: seen };
  }
  function currentTerm() {
    if (lastCommit) return lastCommit.term;
    return lastQuery ? safeTerm(lastQuery, queryClass(lastQuery, 'digits')) : '';
  }
  /* BUY in the results is handled by assets/vip-cart-drawer.js (capture-phase click); it calls these so
     the BUY event carries the same safe, zero-preserving term as the search events. */
  window.VIPSearchTrack = {
    commit: function (reason) { commitSearch(reason || 'buy'); },
    addToCart: function (btn) {
      track('number_search_add_to_cart', {
        search_term: currentTerm(),
        variant_id: (btn && btn.getAttribute('data-vid')) || variantIdFor(btn && btn.getAttribute('data-num')),
        price: Number(btn && btn.getAttribute('data-price')) || 0,
        search_id: sid || '', track_v: '2'
      });
    }
  };
  /* leaving the page, or the tab going to the background, completes the current intention */
  window.addEventListener('pagehide', function () { commitSearch('leave'); });
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') commitSearch('leave'); });

  /* ---------------- search ---------------- */
  function search(q) {
    var out = [];
    if (!groups) return out;
    groups.forEach(function (group) {
      var hits = [];
      var items = group.items;
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it._segs) prepItem(it);            /* on-demand: never depends on idle time */
        if (isDead(it)) continue;               /* known sold / deleted / archived */
        var segs = it._segs;
        for (var j = 0; j < segs.length; j++) {
          if (segs[j].indexOf(q) !== -1) { hits.push(it); break; }
        }
      }
      hits.sort(function (a, b) { return (a[1] || 0) - (b[1] || 0); });
      out.push({ badge: group.badge, hits: hits });
    });
    return out;
  }

  function money(v) {
    v = Number(v) || 0;
    var s = (v % 1 === 0) ? v.toFixed(0) : v.toFixed(2);
    return '£' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function highlight(title, q) {
    /* highlights the typed digits even when they span spaces inside one number */
    var parts = String(title).split(/(&)/);
    var out = '';
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      if (!p) continue;
      var map = [], digits = '';
      for (var k = 0; k < p.length; k++) {
        var c = p.charAt(k);
        if (c >= '0' && c <= '9') { map.push(k); digits += c; }
      }
      var pos = q ? digits.indexOf(q) : -1;
      if (pos === -1) { out += esc(p); continue; }
      var hs = map[pos], he = map[pos + q.length - 1] + 1;
      out += esc(p.slice(0, hs)) +
        '<span style="color:' + GOLD + ';">' + esc(p.slice(hs, he)) + '</span>' +
        esc(p.slice(he));
    }
    return out;
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------------- panel UI ---------------- */
  function ensurePanel() {
    if (panel) return panel;
    panel = document.createElement('div');
    panel.id = 'vip-num-search-panel';
    panel.style.cssText =
      'position:absolute;z-index:100000;display:none;' +
      'background:' + BG + ';border:1px solid ' + BORDER + ';border-radius:8px;' +
      'box-shadow:0 12px 40px rgba(0,0,0,0.6);' +
      'max-height:70vh;overflow-y:auto;-webkit-overflow-scrolling:touch;' +
      'font-family:Jost,sans-serif;color:#fff;';
    document.body.appendChild(panel);

    document.addEventListener('mousedown', function (e) {
      if (!panel || panel.style.display === 'none') return;
      if (panel.contains(e.target)) return;
      if (activeInput && e.target === activeInput) return;
      userClose();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') userClose();
    });
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return panel;
  }

  function position() {
    if (!panel || panel.style.display === 'none' || !activeInput) return;
    var r = activeInput.getBoundingClientRect();
    var w = Math.max(r.width, 300);
    var left = r.left + window.pageXOffset;
    if (left + w > window.pageXOffset + document.documentElement.clientWidth - 8) {
      left = window.pageXOffset + document.documentElement.clientWidth - w - 8;
    }
    panel.style.top = (r.bottom + window.pageYOffset + 6) + 'px';
    panel.style.left = Math.max(8, left) + 'px';
    panel.style.width = w + 'px';
  }

  /* v6.0: the customer closed the results (outside tap, Esc, ×): completes the search intention */
  function userClose() { commitSearch('close'); hidePanel(); }

  function showPanel(html) {
    ensurePanel();
    panel.innerHTML = html;
    panel.style.display = 'block';
    position();
  }
  function hidePanel() {
    userClosed = true;
    if (panel) panel.style.display = 'none';
  }

  function headerRow(text) {
    return '<div style="display:flex;justify-content:space-between;align-items:center;' +
      'padding:10px 14px;border-bottom:1px solid ' + BORDER + ';">' +
      '<div style="font-size:12px;letter-spacing:1px;color:#b8b0a4;">' + text + '</div>' +
      '<div id="vipNumClose" style="cursor:pointer;color:#b8b0a4;font-size:16px;' +
      'padding:2px 6px;line-height:1;">&times;</div></div>';
  }

  function bindClose() {
    var x = document.getElementById('vipNumClose');
    if (x) x.addEventListener('mousedown', function (e) {
      e.preventDefault(); e.stopPropagation(); userClose();
    });
  }

  function footerLine() {
    var html = '<div style="padding:8px 14px 10px;border-top:1px solid ' + BORDER + ';' +
      'font-size:11px;color:' + MUTED + ';">';
    if (status === 'loading') {
      html += 'Loaded ' + loadedCount().toLocaleString() +
        (grandExpected ? ' of ' + grandExpected.toLocaleString() : '') +
        ' numbers (' + pct() + '%) &mdash; results update as more load&hellip;';
    } else if (isIncomplete()) {
      html += 'Searched ' + loadedCount().toLocaleString() + ' numbers ' +
        '<span style="color:' + GOLD + ';">&mdash; some numbers could not be loaded, ' +
        'refresh the page to retry</span>';
    } else {
      html += 'Searched ' + loadedCount().toLocaleString() + ' numbers';
      if (refreshing) html += ' &middot; updating&hellip;';
    }
    if (verifyBroken) {
      html += '<br><span style="color:' + GOLD + ';">Live availability check unavailable &mdash; ' +
        'tap a number to view it.</span>';
    }
    return html + '</div>';
  }

  function scheduleRender(ms) {
    if (!activeInput || !lastQuery || lastQuery.length < MIN_DIGITS) return;
    /* v5.3: background updates only refresh results that are currently visible;
       they never re-open a closed panel */
    if (userClosed || !panel || panel.style.display === 'none') return;
    clearTimeout(renderTimer);
    renderTimer = setTimeout(function () {
      if (activeInput && lastQuery && lastQuery.length >= MIN_DIGITS) requestRender(lastQuery);
    }, ms);
  }

  /* Search locally, verify the rows about to be shown, then render.
     Results appear as soon as the check returns, or after VERIFY_WAIT
     with unverified rows shown without an active BUY. */
  function requestRender(q, round) {
    round = round || 0;
    if (status !== 'ready') { render(q, false); return; }
    var seq = ++renderSeq;
    var vids = unverifiedShown(q);
    if (!vids.length) { render(q, false); return; }

    var settled = false;
    var p = verifyVids(vids);
    var waitT = setTimeout(function () {
      if (settled || seq !== renderSeq || q !== lastQuery) return;
      render(q, true); /* still checking: show rows, BUY disabled */
    }, VERIFY_WAIT);
    p.then(function (ok) {
      settled = true;
      clearTimeout(waitT);
      if (seq !== renderSeq || q !== lastQuery) return;
      /* removing sold numbers can move further numbers into the visible list:
         check those too (bounded), so every visible AVAILABLE row is verified */
      if (ok && round < 3 && unverifiedShown(q).length) {
        render(q, true);
        requestRender(q, round + 1);
        return;
      }
      render(q, false);
    });
  }

  function unverifiedShown(q) {
    var vids = [];
    search(q).forEach(function (g) {
      g.hits.slice(0, MAX_PER_GROUP).forEach(function (it) {
        var v = itemVid(it);
        if (v && !isVerified(it)) vids.push(v);
      });
    });
    return vids;
  }

  function buyCell(it, g, checking) {
    var vid = itemVid(it);
    if (g.badge !== 'AVAILABLE') return '';
    if (vid && isVerified(it)) {
      return '<button type="button" class="vip-num-add" data-vid="' + vid + '" data-num="' + esc(it[0]) + '" data-price="' + (it[1] || 0) + '" ' +
        'style="background:transparent;border:1px solid ' + GOLD + ';color:' + GOLD + ';' +
        'border-radius:5px;padding:5px 10px;font-family:Jost,sans-serif;font-size:11px;' +
        'font-weight:600;letter-spacing:1px;cursor:pointer;min-width:52px;">BUY</button>';
    }
    if (checking && !verifyBroken) {
      return '<button type="button" disabled aria-disabled="true" class="vip-num-wait" ' +
        'style="background:transparent;border:1px solid rgba(212,184,94,0.25);color:' + MUTED + ';' +
        'border-radius:5px;padding:5px 10px;font-family:Jost,sans-serif;font-size:11px;' +
        'font-weight:600;letter-spacing:1px;min-width:52px;cursor:default;">&hellip;</button>';
    }
    /* unverified (check failed / not possible): no active BUY, row opens product page */
    return '<span class="vip-num-view" style="border:1px solid rgba(212,184,94,0.25);color:#b8b0a4;' +
      'border-radius:5px;padding:5px 10px;font-family:Jost,sans-serif;font-size:11px;' +
      'font-weight:600;letter-spacing:1px;min-width:52px;text-align:center;box-sizing:border-box;">VIEW</span>';
  }

  function render(q, checking) {
    if (userClosed) return;   /* v5.3: e.g. a live check finishing after the customer closed the results */
    var qGroups = search(q);
    var total = 0;
    qGroups.forEach(function (g) { total += g.hits.length; });
    shownCounts = { q: q, a: qGroups[0] ? qGroups[0].hits.length : 0, p: qGroups[1] ? qGroups[1].hits.length : 0 };

    if (!total) {
      if (status !== 'ready') {
        showPanel(
          headerRow('SEARCHING&hellip; ' + pct() + '%') +
          '<div style="padding:16px 14px;font-size:13px;color:#b8b0a4;">' +
          'Searching ' + loadedCount().toLocaleString() +
          (grandExpected ? ' of ' + grandExpected.toLocaleString() : '') +
          ' numbers so far&hellip; results will appear here as they are found.</div>'
        );
      } else {
        showPanel(
          headerRow('0 RESULTS') +
          '<div style="padding:16px 14px;font-size:14px;color:#b8b0a4;">' +
          'No numbers found containing <span style="color:' + GOLD + ';">' + esc(q) +
          '</span>.<br><span style="font-size:12px;">Try fewer digits, or ' +
          '<a href="/pages/contact" style="color:' + GOLD + ';">ask us</a> &mdash; ' +
          'we can source numbers on request.<br>Want a specific ending? ' +
          '<a href="/pages/custom-numbers" class="vip-num-custom" style="color:' + GOLD + ';">' +
          'Choose your own last 6 digits &rarr;</a></span></div>' +
          footerLine()
        );
      }
      bindClose();
      return;
    }

    var html = headerRow(total.toLocaleString() + ' NUMBER' + (total === 1 ? '' : 'S') +
      ' CONTAINING &ldquo;' + esc(q) + '&rdquo;' +
      (status === 'loading' ? ' &mdash; ' + pct() + '%' : ''));

    qGroups.forEach(function (g) {
      if (!g.hits.length) return;
      html += '<div style="padding:9px 14px 4px;font-size:11px;letter-spacing:2px;' +
        'color:' + GOLD + ';">' + g.badge + ' (' + g.hits.length.toLocaleString() + ')</div>';
      var shown = g.hits.slice(0, MAX_PER_GROUP);
      shown.forEach(function (it) {
        var handle = itemHandle(it);
        html += '<a href="/products/' + encodeURIComponent(handle) + '" ' +
          'class="vip-num-hit" data-num="' + esc(it[0]) + '" data-price="' + (it[1] || 0) + '" data-group="' + g.badge + '" ' +
          'style="display:flex;justify-content:space-between;align-items:center;' +
          'padding:9px 14px;text-decoration:none;color:#fff;font-size:15px;">' +
          '<span style="font-weight:500;letter-spacing:2px;font-variant-numeric:tabular-nums;">' + highlight(it[0], q) + '</span>' +
          '<span style="display:flex;align-items:center;gap:10px;margin-left:12px;">' +
          '<span style="color:' + GOLD + ';font-size:13px;">' + money(it[1]) + '</span>' +
          buyCell(it, g, checking) +
          '</span></a>';
      });
      if (g.hits.length > MAX_PER_GROUP) {
        html += '<div class="vip-num-more" role="button" tabindex="0" ' +
          'style="padding:6px 14px 10px;font-size:12px;color:#b8b0a4;cursor:pointer;">' +
          'Showing first ' + MAX_PER_GROUP + ' of ' + g.hits.length.toLocaleString() +
          ' &mdash; <span style="color:' + GOLD + ';text-decoration:underline;text-underline-offset:2px;">' +
          'add more digits to narrow down</span></div>';
      }
    });

    html += footerLine();
    showPanel(html);
    bindClose();
    watchBuyButtons();

    /* click via location.assign so vip-scripts.js link hijacking can't swallow it */
    Array.prototype.forEach.call(panel.querySelectorAll('.vip-num-hit'), function (a) {
      a.addEventListener('mouseenter', function () { a.style.background = '#1a1812'; });
      a.addEventListener('mouseleave', function () { a.style.background = 'transparent'; });
      a.addEventListener('click', function (e) {
        if (e.target && e.target.classList &&
            (e.target.classList.contains('vip-num-add') || e.target.classList.contains('vip-num-wait'))) {
          e.preventDefault(); e.stopPropagation();
          return;
        }
        e.preventDefault(); e.stopPropagation();
        commitSearch('click');
        track('number_search_click', {
          search_term: currentTerm(),
          variant_id: variantIdFor(a.getAttribute('data-num')),
          price: Number(a.getAttribute('data-price')) || 0,
          item_group: a.getAttribute('data-group') || '',
          search_id: sid || '', track_v: '2'
        });
        window.location.assign(a.getAttribute('href'));
      });
    });

    Array.prototype.forEach.call(panel.querySelectorAll('.vip-num-add'), function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        addToCart(btn);
      });
    });

    /* tap 'add more digits' → refocus the search box (reopens keyboard on mobile) */
    Array.prototype.forEach.call(panel.querySelectorAll('.vip-num-more'), function (d) {
      function refocus(e) {
        e.preventDefault(); e.stopPropagation();
        if (activeInput) {
          activeInput.focus();
          try {
            var len = activeInput.value.length;
            activeInput.setSelectionRange(len, len);
          } catch (err) { }
          activeInput.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }
      d.addEventListener('click', refocus);
      d.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') refocus(e);
      });
    });
  }

  /* ---------------- BUY ---------------- */
  /* The site's Cart Drawer (vip-cart-drawer.js) intercepts BUY clicks on
     .vip-num-add in the capture phase and performs the add itself, so the
     cart flow and its GA4 event are unchanged. If its add fails it resets
     the button to "BUY". We watch for that and re-check the number live:
     no longer available -> "SOLD"; otherwise -> "RETRY". */
  var buyObserver = null;
  function watchBuyButtons() {
    if (!panel || buyObserver || typeof MutationObserver !== 'function') return;
    buyObserver = new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        var el = m.target && m.target.nodeType === 3 ? m.target.parentNode : m.target;
        if (!el || !el.classList || !el.classList.contains('vip-num-add')) return;
        var txt = (el.textContent || '').trim();
        if (txt === '…') { el.__vipBusy = true; return; }
        if (txt === 'BUY' && el.__vipBusy && !el.disabled && !el.__vipOwn) {
          el.__vipBusy = false;
          handleBuyFailure(el);
        } else if (txt !== 'BUY') {
          el.__vipBusy = false;
        }
      });
    });
    buyObserver.observe(panel, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
  }

  function handleBuyFailure(btn) {
    var vid = parseInt(btn.getAttribute('data-vid'), 10);
    if (!vid) return;
    btn.disabled = true;
    btn.__vipOwn = true;
    btn.textContent = '…';
    if (vstate[vid]) vstate[vid].t = 0; /* force a fresh check */
    verifyVids([vid]).then(function (ok) {
      var s = vstate[vid];
      btn.__vipOwn = false;
      if (s && (s.s === 'gone' || s.s === 'na')) markSold(btn);
      else markRetry(btn);
    });
  }

  function markSold(btn) {
    btn.disabled = true;
    btn.textContent = 'SOLD';
    btn.title = 'This number has just been sold';
    btn.style.background = 'transparent';
    btn.style.color = MUTED;
    btn.style.borderColor = 'rgba(212,184,94,0.25)';
    btn.style.cursor = 'default';
  }
  function markRetry(btn) {
    btn.disabled = false;
    btn.textContent = 'RETRY';
    btn.style.background = 'transparent';
    btn.style.color = GOLD;
  }

  function addToCart(btn) {
    if (btn.disabled) return;
    commitSearch('buy');
    btn.__vipOwn = true;
    btn.disabled = true;
    btn.textContent = '…';
    var vid = parseInt(btn.getAttribute('data-vid'), 10);
    var st = vstate[vid];
    var pre = (st && st.s === 'ok' && (now() - st.t) < BUY_RECHECK)
      ? Promise.resolve(true)
      : verifyVids([vid]);
    pre.then(function (ok) {
      var s = vstate[vid];
      if (s && (s.s === 'gone' || s.s === 'na')) { markSold(btn); return null; }
      if (!ok || !s || s.s !== 'ok') { markRetry(btn); return null; }
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: vid, quantity: 1 })
      }).then(function (r) {
        if (r.ok) return r.json().then(function () { return 'ok'; });
        /* 404/422: Shopify could not add it — the number is no longer available */
        if (r.status === 404 || r.status === 422) return 'sold';
        return 'retry';
      }, function () { return 'retry'; }).then(function (res) {
        if (res === 'sold') {
          vstate[vid] = { s: 'na', t: now() };
          markSold(btn);
          return;
        }
        if (res !== 'ok') { markRetry(btn); return; }
        btn.textContent = '✓';
        btn.style.background = GOLD;
        btn.style.color = '#0f0e0a';
        track('number_search_add_to_cart', {
          search_term: currentTerm(),
          variant_id: btn.getAttribute('data-vid') || variantIdFor(btn.getAttribute('data-num')),
          price: Number(btn.getAttribute('data-price')) || 0,
          search_id: sid || '', track_v: '2'
        });
        return fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var badge = document.getElementById('cartBadge');
            if (badge && cart && typeof cart.item_count === 'number') {
              badge.textContent = cart.item_count;
              badge.style.display = 'flex';
            }
          }).catch(function () { });
      });
    }).catch(function () { markRetry(btn); });
  }

  /* ---------------- input wiring ---------------- */
  function digitsOf(v) { return String(v || '').replace(/\D/g, ''); }

  /* v5.2: UK international mobile format -> national format.
     "+44 7517 515555", "0044 7517 515555", "+44 (0)7517 515555", "447517515555"
     all become "07517515555". Only a UK mobile (44 followed by 7) is converted.
     Without an explicit "+", only a complete number (447xxxxxxxxx, 4407xxxxxxxxx,
     00447xxxxxxxxx or 004407xxxxxxxxx) is converted, so ordinary partial searches
     such as "447", "4475" or "0044" are left exactly as typed. */
  function queryOf(v) {
    var raw = String(v || '');
    var d = digitsOf(raw);
    if (/^\s*\+\s*44/.test(raw)) {
      var rest = d.replace(/^44/, '');
      if (rest.charAt(0) === '0') rest = rest.slice(1);      /* "+44 (0)7..." */
      if (rest.charAt(0) === '7') return '0' + rest;
      return d;
    }
    if (/^00447\d{9}$/.test(d)) return '0' + d.slice(4);
    if (/^004407\d{9}$/.test(d)) return d.slice(4);
    if (/^447\d{9}$/.test(d)) return '0' + d.slice(2);
    if (/^4407\d{9}$/.test(d)) return d.slice(2);
    return d;
  }

  function onInput(input) {
    var q = queryOf(input.value);
    lastQuery = q;
    activeInput = input;
    userClosed = false;       /* v5.3: the customer is searching again */
    noteInput(input, q);      /* v6.0: analytics only */
    if (q.length < MIN_DIGITS) { hidePanel(); return; }
    var p = startLoading();
    requestRender(q);
    if (status !== 'ready' && p && p.then) {
      p.then(function () { if (q === lastQuery) requestRender(q); });
    }
  }

  function shouldAttach(el) {
    if (!el || el.tagName !== 'INPUT') return false;
    if (el.__vipNumSearch) return false;
    if (el.hasAttribute('data-vip-search')) return true;
    var ph = (el.getAttribute('placeholder') || '').toLowerCase();
    return ph.indexOf('search') !== -1 && ph.indexOf('number') !== -1;
  }

  function attach(el) {
    el.__vipNumSearch = true;
    var t = null;
    el.addEventListener('focus', function () {
      activeInput = el;
      startLoading(); /* pre-warm on intent */
    });
    el.addEventListener('input', function (e) {
      /* v6.0 analytics: how the text arrived (typed / pasted / autofilled), never the text itself */
      var it = (e && e.inputType) || '';
      el.__vipLastInputType = /paste|drop/i.test(it) ? 'paste' : (/replacement/i.test(it) || !it ? 'autofill' : 'typed');
      clearTimeout(t);
      t = setTimeout(function () { onInput(el); }, 120);
    });
    el.addEventListener('keydown', function (e) { if (e.key === 'Enter') commitSearch('enter'); });
    el.addEventListener('blur', function () { setTimeout(function () { commitSearch('blur'); }, 300); });
  }

  function scan(root) {
    var list = (root || document).querySelectorAll('input');
    Array.prototype.forEach.call(list, function (el) {
      if (shouldAttach(el)) attach(el);
    });
  }

  /* catch inputs injected later (nav overlay etc.) */
  document.addEventListener('focusin', function (e) {
    if (shouldAttach(e.target)) {
      attach(e.target);
      activeInput = e.target;
      startLoading();
    }
  });

  /* v5.3: a page restored from the browser's back/forward cache starts with results closed */
  window.addEventListener('pageshow', function (e) { if (e.persisted) hidePanel(); });

  /* when the tab comes back, run due maintenance (fresh additions / refresh) */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && status === 'ready') scheduleMaintenance(500);
  });

  function suppressLegacyDropdown() {
    try {
      var st = document.createElement('style');
      st.textContent = '#navDropdown{display:none !important;}';
      (document.head || document.documentElement).appendChild(st);
    } catch (e) { }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { suppressLegacyDropdown(); scan(); });
  } else {
    suppressLegacyDropdown();
    scan();
  }
})();
